/**
 * Template Specification for "barAndBubble" Chart
 *
 * This template defines the structure and default configuration for a "bar_and_bubble" chart.
 *
 * Key Categories:
 * 1. Standard Fields:
 *    - 'type': Specifies the chart type (here, "bar_and_bubble").
 *    - 'bar_metric', 'bubble_metric', 'title', and 'subtitle': Define the primary data fields.
 *      Their values start with "$", indicating that these fields expect dynamic inputs (e.g., a metric or a string).
 *
 * 2. Label Fields:
 *    - Keys prefixed with a single underscore (e.g., '_type', '_bar_metric', '_bubble_metric', '_title', '_subtitle')
 *      provide human-readable labels for the corresponding standard fields.
 *
 * 3. Default and Dependency Fields:
 *    - Keys prefixed with double underscores (e.g., '__title', '__subtitle') serve special roles:
 *         - A value starting with "%" (e.g., '__title': '%Bar And Bubble') indicates a default value,
 *           where the "%" is stripped to yield the default (in this case, "Bar And Bubble").
 *         - A value starting with "$$" (e.g., '__subtitle': '$$bar_metric') sets up a dependency;
 *           here, the subtitle should dynamically reflect the metric name associated with the 'bar_metric' field.
 *
 * This specification allows for flexible chart configurations by combining dynamic inputs, default values,
 * and inter-field dependencies to create a rich, customizable chart setup.
 */
export const barAndBubble = {
        'type': 'bar_and_bubble_chart',
        '_type': 'Bar And Bubble',
        'bar_metric': '$metric',
        '_bar_metric': 'Bar Metric',
        'bubble_metric': '$metric',
        '_bubble_metric': "Bubble Metric",
        'title': '$string',
        '_title': 'Title',
        '__title': '%Bar And Bubble',
        'subtitle': '$string',
        '_subtitle': 'Subtitle',
        '__subtitle': '$$bar_metric',
}

// Example chart configuration for "bar_and_bubble" chart. Used in preview mode
export const barAndBubbleExample = (title, bar_metric_label, bubble_metric_label)=> ({
        "chart": {
            "type": "column",
            "zoomType": "xy",
            "animation": false,
            "events": {},
            "height": "100%"
        },
        "title": {
            "text": title
        },
        "xAxis": {
            "categories": [
                "03-04",
                "03-06",
                "03-08"
            ],
            "gridLineWidth": 2,
            "lineColor": "#ccd6eb",
            "lineWidth": 1,
            "labels": {
                "rotation": 0
            },
            "tickmarkPlacement": "between",
            "tickWidth": 1
        },
        "drilldown": {
            "activeAxisLabelStyle": {
                "cursor": "default",
                "color": "rgb(51, 51, 51)",
                "textDecoration": "none",
                "fontWeight": 500,
                "tickmarkPlacement": "between",
                "tickWidth": 1
            },
            "activeDataLabelStyle": {
                "cursor": "default",
                "color": "rgb(51, 51, 51)",
                "textDecoration": "none"
            },
            "series": []
        },
        "yAxis": [
            {
                "title": {
                    "text": ""
                },
                "tickAmount": 6,
                "plotLines": [
                    {
                        "id": "avg",
                        "dashStyle": "Dash",
                        "color": "black",
                        "value": "15.6",
                        "zIndex": 5,
                        "label": {
                            "text": "Avg: 15.6",
                            "align": "right",
                            "style": {
                                "zIndex": 60001
                            }
                        }
                    }
                ]
            },
            {
                "title": {
                    "text": ""
                },
                "min": 0,
                "opposite": true,
                "gridLineWidth": 0,
                "plotLines": [],
                "id": "acwr"
            }
        ],
        "legend": {
            "enabled": true,
            "verticalAlign": "top",
            "layout": "horizontal"
        },
        "tooltip": {
            "shared": true,
            "outside": true
        },
        "plotOptions": {
            "column": {
                "borderWidth": 0,
                "groupPadding": 0.05,
                "pointPadding": 0.05,
                "dataLabels": {
                    "enabled": true,
                    "inside": true,
                    "y": 0,
                    "rotation": 0,
                    "style": {
                        "textOutline": "none"
                    }
                }
            },
            "spline": {
                "dataLabels": {
                    "style": {
                        "textOutline": "none"
                    }
                }
            },
            "series": {
                "animation": false,
                "marker": {
                    "enabled": true
                },
                "events": {}
            }
        },
        "colors": [
            "#31B452",
            "#003262",
            "#FDB515",
            "#3B7EA1",
            "#C4820E",
            "#4797ff",
        ],
        "series": [
            {
                "name": bar_metric_label,
                "type": "column",
                "yAxis": 0,
                "data": [
                    {
                        "name": "03-04",
                        "y": 14.8,
                        "color": "#31B452",
                        "restTravelLabel": "",
                        "drilldown": "drilldown-0"
                    },
                    {
                        "name": "03-06",
                        "y": 16.4,
                        "color": "#31B452",
                        "restTravelLabel": "",
                        "drilldown": "drilldown-1"
                    },
                    {
                        "name": "03-08",
                        "y": 15.6,
                        "color": "#31B452",
                        "restTravelLabel": "",
                        "drilldown": "drilldown-2"
                    }
                ],
                "colorByPoint": false,
                "dataLabels": {
                    "allowOverlap": true,
                    "style": {
                        "textOutline": "none"
                    }
                }
            },
            {
                "name": bubble_metric_label,
                "type": "scatter",
                "yAxis": 0,
                "id": "secondary",
                "data": [
                    {
                        "y": 14.0,
                        "bubbleValue": 74.3,
                        "marker": {
                            "enabled": false
                        },
                        "dataLabels": {
                            "enabled": true,
                            "style": {
                                "textOutline": "none",
                                "fontWeight": "normal",
                                "fontSize": "11px",
                                "stroke": "#31B452"
                            },
                            "backgroundColor": "white",
                            "borderRadius": 12,
                            "borderWidth": 1,
                            "borderColor": "black",
                            "color": "#003262",
                            "padding": 5,
                            "shape": "circle",
                            "verticalAlign": "middle",
                            "align": "center",
                            "y": -17,
                            "x": 0,
                            "useHTML": true
                        }
                    },
                    {
                        "y": 16.4,
                        "bubbleValue": 82.4,
                        "marker": {
                            "enabled": false
                        },
                        "dataLabels": {
                            "enabled": true,
                            "style": {
                                "textOutline": "none",
                                "fontWeight": "normal",
                                "fontSize": "11px",
                                "stroke": "#31B452"
                            },
                            "backgroundColor": "white",
                            "borderRadius": 12,
                            "borderWidth": 1,
                            "borderColor": "black",
                            "color": "#003262",
                            "padding": 5,
                            "shape": "circle",
                            "verticalAlign": "middle",
                            "align": "center",
                            "y": -17,
                            "x": 0,
                            "useHTML": true
                        }
                    },
                    {
                        "y": 15.6,
                        "bubbleValue": 78.1,
                        "marker": {
                            "enabled": false
                        },
                        "dataLabels": {
                            "enabled": true,
                            "style": {
                                "textOutline": "none",
                                "fontWeight": "normal",
                                "fontSize": "11px",
                                "stroke": "#31B452"
                            },
                            "backgroundColor": "white",
                            "borderRadius": 12,
                            "borderWidth": 1,
                            "borderColor": "black",
                            "color": "#003262",
                            "padding": 5,
                            "shape": "circle",
                            "verticalAlign": "middle",
                            "align": "center",
                            "y": -17,
                            "x": 0,
                            "useHTML": true
                        }
                    }
                ],
                "selected": false,
                "dataLabels": {
                    "enabled": true,
                    "allowOverlap": true
                }
            },
            {
                "name": "ACWR",
                "type": "spline",
                "yAxis": 1,
                "data": [],
                "color": "rgb(70, 170, 197)",
                "marker": {
                    "enabled": true,
                    "radius": 3,
                    "symbol": "circle"
                },
                "selected": true,
                "showInLegend": false,
                "connectNulls": true
            }
        ],
        "exporting": {
            "enabled": false
        }
    })